import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import Select from "react-select";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import {
    getAllEvents,
    getBookingByEventId,
    getBookingDetailsV2,
    getBookingDetails,
    refundAllByEventId,
    refundByBookingNo
} from "api";

const Refund = () => {
    document.title = "Refunds | EventoX";
    const navigate = useNavigate();

    const [eventList, setEventList] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const searchByOptions = [
        { value: 'event', label: 'Event' },
        { value: 'booking_number', label: 'Booking Number' }
    ];
    const [searchBy, setSearchBy] = useState(searchByOptions[0]);
    const [bookingNumber, setBookingNumber] = useState("");
    const [bookingList, setBookingList] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refundLoading, setRefundLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [stateRestored, setStateRestored] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);

    const gridRef = useRef(null);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const eventsResp = await getAllEvents();
            const eventOptions = eventsResp.map(item => ({
                value: item.eventId,
                label: item.eventName,
                slugId: item.slugId
            }));
            setEventList(eventOptions);

            const savedState = sessionStorage.getItem("refundSearchState");
            if (savedState) {
                try {
                    const parsedState = JSON.parse(savedState);
                    if (parsedState) {
                        const restoredEvent = parsedState.selectedEvent
                            ? eventOptions.find(e => e.value === parsedState.selectedEvent.value) || parsedState.selectedEvent
                            : null;

                        setSelectedEvent(restoredEvent);
                        setSearchBy(parsedState.searchBy || searchByOptions[0]);
                        setBookingNumber(parsedState.bookingNumber || "");
                        setBookingList(parsedState.bookingList || []);
                        if (parsedState.hasSearched) setHasSearched(true);

                        if (parsedState.bookingList?.length > 0) {
                            setStateRestored(true);
                            setTimeout(() => setStateRestored(false), 3000);
                        }
                    }
                } catch (err) {
                    console.error("Error restoring refund state:", err);
                }
            }
        } catch (err) {
            console.error("Error fetching events:", err);
            setError("Failed to load events");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (bookingList.length > 0) {
            const refundState = {
                selectedEvent,
                searchBy,
                bookingNumber,
                bookingList,
                hasSearched: true
            };
            sessionStorage.setItem("refundSearchState", JSON.stringify(refundState));
        }
    }, [bookingList]);

    const clearRefundState = () => {
        sessionStorage.removeItem("refundSearchState");
    };

    const handleSearch = async () => {
        setHasSearched(true);
        setLoading(true);
        setError("");
        setSuccessMessage("");
        setBookingList([]);
        setSelectedRows([]);

        try {
            if (searchBy.value === 'event') {
                if (!selectedEvent) {
                    setError("Please select an event");
                    setLoading(false);
                    return;
                }
                const response = await getBookingByEventId(selectedEvent.value);

                let primaryAttendees = response.filter(booking =>
                    Number(booking.bookedBy) === 0 &&
                    booking.paymentStatus !== 'pending'&&
                    booking.paymentStatus !== 'failed'&&
                    booking.paymentStatus !== 'expired'
                    
                );

                if (bookingNumber.trim()) {
                    const searchNum = bookingNumber.trim().toLowerCase();
                    primaryAttendees = primaryAttendees.filter(booking =>
                        booking.bookingNo?.toLowerCase().includes(searchNum) ||
                        booking.primaryBookingNo?.toLowerCase().includes(searchNum)
                    );
                }

                setBookingList(primaryAttendees);

            } else if (searchBy.value === 'booking_number') {
                if (!bookingNumber.trim()) {
                    setError("Please enter a booking number");
                    setLoading(false);
                    return;
                }
                try {
                    const response = await getBookingDetailsV2(bookingNumber.trim());
                    const data = response?.data || response;

                    if (data) {
                        if (
                            data.paymentStatus === 'paid' &&
                            Number(data.bookedBy) === 0 &&
                            (!selectedEvent || data.Event?.eventId === selectedEvent.value)
                        ) {
                            setBookingList([data]);
                        } else {
                            setError(selectedEvent
                                ? "No paid primary booking found for this event with this number."
                                : "No paid primary booking found with this number."
                            );
                        }
                    } else {
                        setError("No booking found with this number.");
                    }
                } catch (e) {
                    if (e?.response?.status === 404 || !e?.response) {
                        try {
                            const fallbackResponse = await getBookingDetails(bookingNumber.trim());
                            if (fallbackResponse) {
                                if (
                                    fallbackResponse.paymentStatus === 'paid' &&
                                    Number(fallbackResponse.bookedBy) === 0 &&
                                    (!selectedEvent || fallbackResponse.Event?.eventId === selectedEvent.value)
                                ) {
                                    setBookingList([fallbackResponse]);
                                } else {
                                    setError(selectedEvent
                                        ? "No paid primary booking found for this event with this number."
                                        : "No paid primary booking found with this number."
                                    );
                                }
                            } else {
                                setError("No booking found with this number.");
                            }
                        } catch (err) {
                            setError("No booking found with this number.");
                        }
                    } else {
                        setError("Something went wrong. Please try again.");
                    }
                }
            }
        } catch (err) {
            console.error("Error searching bookings:", err);
            setError("Failed to search bookings. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Opens confirmation modal
    const handleRefundClick = () => {
        if (selectedRows.length === 0) {
            setError("Please select at least one attendee to refund.");
            setTimeout(() => setError(""), 3000);
            return;
        }
        setShowRefundModal(true);
    };

    // Called when user confirms inside the modal
    const handleConfirmRefund = async () => {
        setRefundLoading(true);
        setError("");
        setSuccessMessage("");

        try {
            // If all bookings selected and event chosen → use refund-all endpoint
            if (selectedRows.length === bookingList.length && selectedEvent) {
                const response = await refundAllByEventId(selectedEvent.value);

                if (response?.success === true) {
                    const totalRefunded = response?.data?.totalRefunded || response?.totalRefunded || 0;
                    const totalFailed = response?.data?.totalFailed || response?.totalFailed || 0;
                    const results = response?.data?.results || response?.results || [];

                    if (totalRefunded === 0 && totalFailed === 0) {
                        setSuccessMessage("Refund processed.");
                        setShowRefundModal(false);
                        await handleSearch();
                        return;
                    }

                    const failedNos = results
                        .filter(r => !r.success)
                        .map(r => r.paymentReference || r.bookingNo)
                        .join(", ");

                    setSuccessMessage(
                        `${totalRefunded} booking(s) refunded successfully.` +
                        (totalFailed > 0 ? ` ${totalFailed} failed: ${failedNos}` : "")
                    );
                    setShowRefundModal(false);
                    await handleSearch();
                } else {
                    setError(response?.message || "Refund failed. Please try again.");
                    setShowRefundModal(false);
                }
            } else {
                // Individual refunds by bookingNo
                const results = await Promise.allSettled(
                    selectedRows.map(row => refundByBookingNo(row.bookingNo))
                );

                const succeeded = [];
                const failed = [];

                const has403 = results.some(
                    r => r.status === 'rejected' && r.reason?.response?.status === 403
                );
                if (has403) {
                    setError("Only admin can perform refunds.");
                    setShowRefundModal(false);
                    setRefundLoading(false);
                    return;
                }

                results.forEach((result, index) => {
                    const bookingNo = selectedRows[index].bookingNo;
                    if (result.status === 'fulfilled' && result.value?.success) {
                        succeeded.push(bookingNo);
                    } else {
                        const status = result.reason?.response?.status;
                        if (status === 404) {
                            failed.push(`${bookingNo} (not eligible)`);
                        } else if (status === 400) {
                            failed.push(`${bookingNo} (could not process)`);
                        } else {
                            failed.push(`${bookingNo}${result.value?.message ? ` (${result.value.message})` : ''}`);
                        }
                    }
                });

                setShowRefundModal(false);

                if (succeeded.length > 0) {
                    setSuccessMessage(
                        `${succeeded.length} booking(s) refunded successfully.` +
                        (failed.length > 0 ? ` Failed: ${failed.join(", ")}` : "")
                    );
                    await handleSearch();
                } else if (failed.length > 0 && !error) {
                    setError(`Refund failed for: ${failed.join(", ")}`);
                }
            }
        } catch (err) {
            console.error("Refund error:", err);
            const status = err?.response?.status;
            if (status === 403) {
                setError("Only admin can perform refunds.");
            } else if (status === 404) {
                setError("Booking not found or not eligible for refund.");
            } else if (status === 400) {
                setError("Could not process refund. Please try again.");
            } else {
                setError("Refund failed. Please try again.");
            }
            setShowRefundModal(false);
        } finally {
            setRefundLoading(false);
        }
    };

    // Summary values for the modal
    const totalBookings = selectedRows.length;
    const totalTickets = selectedRows.reduce((sum, row) => {
        if (Number(row.bookedBy) === 0) {
            return sum + (parseInt(row.totalTicketCount, 10) || 0);
        }
        return sum;
    }, 0);
    const totalRefundAmount = selectedRows.reduce((sum, row) => sum + (row.grandTotal || 0), 0);

    const onSelectionChanged = () => {
        if (gridRef.current) {
            setSelectedRows(gridRef.current.api.getSelectedRows());
        }
    };

    const isSearchValid = () => {
        if (searchBy.value === 'event') {
            return selectedEvent !== null;
        } else if (searchBy.value === 'booking_number') {
            return bookingNumber.trim().length > 0;
        }
        return false;
    };

    const columnDefs = useMemo(() => {
        return [
            {
                headerName: 'Booking No',
                field: 'bookingNo',
                filter: true,
                sortable: true,
                width: 180,
                checkboxSelection: true,
                headerCheckboxSelection: true
            },
            {
                headerName: 'Name',
                valueGetter: params => {
                    const contact = params.data.bookingObject?.primaryContactDetails || params.data.Contact || {};
                    return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
                },
                filter: true,
                sortable: true,
                width: 150
            },
            {
                headerName: 'Email',
                valueGetter: params => {
                    const contact = params.data.bookingObject?.primaryContactDetails || params.data.Contact || {};
                    return contact.email || '';
                },
                filter: true,
                sortable: true,
                width: 200
            },
            {
                headerName: 'Phone',
                valueGetter: params => {
                    const contact = params.data.bookingObject?.primaryContactDetails || params.data.Contact || {};
                    return contact.phoneNo || '';
                },
                filter: true,
                sortable: true,
                width: 120
            },
            {
                headerName: 'Ticket Type',
                valueGetter: params => params.data.TicketType?.ticketType || '',
                filter: true,
                sortable: true,
                width: 150
            },
            {
                headerName: 'Quantity',
                field: 'totalTicketCount',
                filter: true,
                sortable: true,
                width: 100
            },
            {
                headerName: 'Total Amount',
                field: 'grandTotal',
                filter: true,
                sortable: true,
                width: 120,
                valueFormatter: params => `$${Number(params.value || 0).toFixed(2)}`
            },
            {
                headerName: 'Payment Status',
                field: 'paymentStatus',
                filter: true,
                sortable: true,
                width: 120,
                cellRenderer: params => {
                    const status = params.value;
                    let badgeClass = 'bg-secondary';
                    if (status === 'paid') badgeClass = 'bg-success';
                    else if (status === 'pending') badgeClass = 'bg-warning';
                    else if (status === 'cancelled') badgeClass = 'bg-danger';
                    else if (status === 'expired') badgeClass = 'bg-warning';
                    else if (status === 'cancelled_by_user') badgeClass = 'bg-danger';
                    return <span className={`badge ${badgeClass}`}>{status}</span>;
                }
            },
            {
                headerName: 'Booking Date',
                field: 'createdAt',
                filter: true,
                sortable: true,
                width: 150,
                valueFormatter: params => moment(params.value).format('MMM DD, YYYY HH:mm')
            },
            {
                headerName: 'Actions',
                field: 'actions',
                width: 120,
                sortable: false,
                filter: false,
                cellRenderer: params => {
                    return (
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                                window.open(`/admin/report/booking-details/${params.data.primaryBookingNo || params.data.bookingNo}`, "_blank");
                            }}
                            className="btn-sm"
                        >
                            <i className="bx bx-show me-1"></i>
                            View Details
                        </Button>
                    );
                },
            }
        ];
    }, []);

    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 120,
        maxWidth: 300,
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        suppressMenu: false,
        suppressMovable: false,
    }), []);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Refund" breadcrumbItem="Refund Search" />

                    <Card>
                        <CardBody>
                            <h4 className="card-title mb-4">Search Bookings for Refund</h4>

                            <Form>
                                <Row>
                                    <Col lg={3} md={6}>
                                        <FormGroup>
                                            <Label>Search By</Label>
                                            <Select
                                                value={searchBy}
                                                onChange={(option) => {
                                                    setSearchBy(option);
                                                    setBookingList([]);
                                                    setError("");
                                                    setSuccessMessage("");
                                                    if (option.value === 'booking_number') {
                                                        setSelectedEvent(null);
                                                    }
                                                }}
                                                options={searchByOptions}
                                                placeholder="Search By"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                    menu: (base) => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>

                                    <Col lg={3} md={6}>
                                        <FormGroup>
                                            <Label>Select Event</Label>
                                            <Select
                                                value={selectedEvent}
                                                onChange={setSelectedEvent}
                                                options={eventList}
                                                placeholder="Select event"
                                                isClearable
                                                isDisabled={searchBy.value === 'booking_number'}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                    menu: (base) => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>

                                    <Col lg={3} md={6}>
                                        <FormGroup>
                                            <Label>Booking Number</Label>
                                            <Input
                                                type="text"
                                                value={bookingNumber}
                                                onChange={(e) => setBookingNumber(e.target.value)}
                                                placeholder="Enter booking number"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="align-items-center">
                                    <Col lg={2} md={6} className="d-flex align-items-end mt-3">
                                        <Button
                                            color="primary"
                                            onClick={handleSearch}
                                            disabled={loading || !isSearchValid()}
                                            className="w-100"
                                        >
                                            {loading ? 'Searching...' : 'Search'}
                                        </Button>
                                    </Col>

                                    <Col lg={1} md={6} className="d-flex align-items-end mt-3">
                                        <Button
                                            color="secondary"
                                            onClick={() => {
                                                setSelectedEvent(null);
                                                setSearchBy(searchByOptions[0]);
                                                setBookingNumber("");
                                                setBookingList([]);
                                                setSelectedRows([]);
                                                setError("");
                                                setSuccessMessage("");
                                                setHasSearched(false);
                                                if (gridRef.current) {
                                                    gridRef.current.api.deselectAll();
                                                }
                                                clearRefundState();
                                            }}
                                            className="w-100"
                                        >
                                            Clear
                                        </Button>
                                    </Col>

                                    {/* FIX 1: Removed !isAdmin from disabled condition */}
                                    <Col lg={2} md={6} className="d-flex align-items-end mt-3 ms-auto">
                                        <Button
                                            color="success"
                                            onClick={handleRefundClick}
                                            disabled={selectedRows.length === 0 || refundLoading}
                                            className="w-100"
                                        >
                                            <i className="bx bx-money me-1"></i>
                                            Refund ({selectedRows.length})
                                        </Button>
                                    </Col>

                                    {/* FIX 1: Removed !isAdmin from disabled condition */}
                                    <Col lg={2} md={6} className="d-flex align-items-end mt-3">
                                        <Button
                                            color="danger"
                                            disabled={bookingList.length === 0 || refundLoading}
                                            className="w-100"
                                            onClick={() => {
                                                if (!selectedEvent) {
                                                    setError("Please select an event to refund all.");
                                                    setTimeout(() => setError(""), 3000);
                                                    return;
                                                }
                                                // FIX 4: Synchronously read selected rows after selectAll
                                                // to prevent race condition where modal opens with 0 rows
                                                gridRef.current.api.selectAll();
                                                const rows = gridRef.current.api.getSelectedRows();
                                                setSelectedRows(rows);
                                                setShowRefundModal(true);
                                            }}
                                        >
                                            <i className="bx bx-transfer me-1"></i>
                                            Refund All ({bookingList.length})
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>

                    {error && (
                        <Alert color="danger" className="mt-3" toggle={() => setError("")}>
                            {error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert color="success" className="mt-3" toggle={() => setSuccessMessage("")}>
                            <i className="bx bx-check-circle me-2"></i>
                            {successMessage}
                        </Alert>
                    )}

                    {stateRestored && (
                        <Alert color="info" className="mt-3" toggle={() => setStateRestored(false)}>
                            <i className="bx bx-refresh me-2"></i>
                            Previous search results have been restored. You can continue from where you left off.
                        </Alert>
                    )}

                    {hasSearched && !loading && bookingList.length === 0 && !error && (
                        <Alert color="info" className="mt-3">
                            No paid primary bookings found matching your search criteria.
                        </Alert>
                    )}

                    {bookingList.length > 0 && (
                        <Card className="mt-4">
                            <CardBody>
                                <h4 className="card-title mb-4">
                                    Search Results
                                    <span className="text-muted fs-6 ms-2">
                                        ({bookingList.length} booking{bookingList.length !== 1 ? 's' : ''})
                                    </span>
                                </h4>
                                <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                                    <AgGridReact
                                        ref={gridRef}
                                        columnDefs={columnDefs}
                                        rowData={bookingList}
                                        defaultColDef={defaultColDef}
                                        pagination={true}
                                        paginationPageSize={50}
                                        paginationPageSizeSelector={[25, 50, 100, 200]}
                                        domLayout="normal"
                                        rowSelection="multiple"
                                        rowMultiSelectWithClick={true}
                                        onSelectionChanged={onSelectionChanged}
                                        suppressRowClickSelection={true}
                                        enableCellTextSelection={true}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {/* Refund Confirmation Modal */}
                    <Modal
                        isOpen={showRefundModal}
                        toggle={() => { if (!refundLoading) setShowRefundModal(false); }}
                        centered
                        size="md"
                        zIndex={10000}
                    >
                        <ModalHeader toggle={() => { if (!refundLoading) setShowRefundModal(false); }}>
                            Refund Confirmation
                        </ModalHeader>
                        <ModalBody>
                            <Alert color="warning">
                                You are about to refund <strong>{totalBookings}</strong> booking(s) with a total refund amount of <strong>${totalRefundAmount.toFixed(2)}</strong>. This action cannot be undone.
                            </Alert>

                            <div className="mb-4">
                                <strong>Selected Bookings:</strong> {totalBookings}<br />
                                <strong>Total Tickets:</strong> {totalTickets}<br />
                                <strong>Total Refund:</strong> ${totalRefundAmount.toFixed(2)}
                            </div>

                            <h6 className="mb-3">Preview:</h6>
                            <div className="table-responsive mb-2">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Booking No</th>
                                            <th>Name</th>
                                            <th className="text-end">Refund Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedRows.slice(0, 3).map((row) => {
                                            const contact = row.bookingObject?.primaryContactDetails || row.Contact || {};
                                            const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
                                            return (
                                                <tr key={row.bookingNo}>
                                                    <td>{row.bookingNo}</td>
                                                    <td>{name || '—'}</td>
                                                    <td className="text-end">${row.grandTotal?.toFixed(2) || '0.00'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {totalBookings > 3 && (
                                <p className="text-muted small text-center">
                                    Showing 3 of {totalBookings} bookings
                                </p>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="secondary"
                                onClick={() => setShowRefundModal(false)}
                                disabled={refundLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="danger"
                                onClick={handleConfirmRefund}
                                disabled={refundLoading}
                            >
                                {refundLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </>
                                ) : (
                                    "Confirm Refund"
                                )}
                            </Button>
                        </ModalFooter>
                    </Modal>

                </Container>
            </div>
        </React.Fragment>
    );
};

export default Refund;